type StreamJsonSseOptions<TMessage, TComplete = void> = {
    fetchFn?: typeof fetch;
    url: string;
    requestInit: RequestInit;
    onMessage: (message: TMessage) => void;
    onError?: (error: Error) => void;
    onComplete?: (result?: TComplete) => void;
    isTerminalEvent: (message: TMessage) => boolean;
    getErrorMessage?: (message: TMessage) => string | undefined;
    getCompletionResult?: (message: TMessage) => TComplete | undefined;
};

type JsonSseCallbacks<TMessage, TComplete> = Pick<
    StreamJsonSseOptions<TMessage, TComplete>,
    | 'onMessage'
    | 'onError'
    | 'onComplete'
    | 'isTerminalEvent'
    | 'getErrorMessage'
    | 'getCompletionResult'
>;

async function fetchSseReader(
    fetchFn: typeof fetch,
    url: string,
    requestInit: RequestInit,
    signal: AbortSignal,
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
    const response = await fetchFn(url, {
        ...requestInit,
        signal,
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader() ?? null;
    if (!reader) {
        throw new Error('No response body');
    }

    return reader;
}

function decodeSseChunk<TMessage>({
    buffer,
    value,
    decoder,
}: {
    buffer: string;
    value: Uint8Array;
    decoder: TextDecoder;
}): { nextBuffer: string; messages: TMessage[] } {
    const fullBuffer = buffer + decoder.decode(value, { stream: true });
    const lines = fullBuffer.split('\n');

    return {
        nextBuffer: lines.pop() ?? '',
        messages: lines
            .filter((line) => line.startsWith('data: '))
            .map((line) => JSON.parse(line.substring(6)) as TMessage),
    };
}

function handleSseMessage<TMessage, TComplete>({
    message,
    callbacks,
}: {
    message: TMessage;
    callbacks: JsonSseCallbacks<TMessage, TComplete>;
}): boolean {
    callbacks.onMessage(message);
    if (!callbacks.isTerminalEvent(message)) {
        return false;
    }

    const errorMessage = callbacks.getErrorMessage?.(message);
    if (errorMessage) {
        callbacks.onError?.(new Error(errorMessage));
    }
    callbacks.onComplete?.(callbacks.getCompletionResult?.(message));
    return true;
}

async function consumeJsonSseStream<TMessage, TComplete>({
    reader,
    callbacks,
    isAborted,
    abort,
}: {
    reader: ReadableStreamDefaultReader<Uint8Array>;
    callbacks: JsonSseCallbacks<TMessage, TComplete>;
    isAborted: () => boolean;
    abort: () => void;
}): Promise<void> {
    const decoder = new TextDecoder();
    let buffer = '';

    while (!isAborted()) {
        const { done, value } = await reader.read();
        if (done) {
            return;
        }

        const decoded = decodeSseChunk<TMessage>({
            buffer,
            value,
            decoder,
        });
        buffer = decoded.nextBuffer;

        for (const message of decoded.messages) {
            if (!handleSseMessage({ message, callbacks })) {
                continue;
            }

            abort();
            return;
        }
    }
}

export async function streamJsonSse<TMessage, TComplete = void>({
    fetchFn = fetch,
    url,
    requestInit,
    onMessage,
    onError,
    onComplete,
    isTerminalEvent,
    getErrorMessage,
    getCompletionResult,
}: StreamJsonSseOptions<TMessage, TComplete>): Promise<() => void> {
    const abortController = new AbortController();
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let aborted = false;
    const abort = () => {
        aborted = true;
    };

    const runSse = async () => {
        try {
            reader = await fetchSseReader(fetchFn, url, requestInit, abortController.signal);
            await consumeJsonSseStream({
                reader,
                callbacks: {
                    onMessage,
                    onError,
                    onComplete,
                    isTerminalEvent,
                    getErrorMessage,
                    getCompletionResult,
                },
                isAborted: () => aborted,
                abort,
            });
        } catch (error) {
            if (aborted) {
                return;
            }

            onError?.(error instanceof Error ? error : new Error('Unknown SSE error'));
            onComplete?.();
        }
    };

    void runSse();

    return () => {
        abort();
        abortController.abort();
        reader?.cancel().catch(() => undefined);
    };
}
